import { useEffect, useState } from 'react';
import {
  doc, onSnapshot, setDoc, updateDoc, collection, onSnapshot as onSnap,
  serverTimestamp, addDoc, query, orderBy,
} from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { coachLinkUrl, createCoachLink, isLiveCoachLink } from '../utils/coachLinks';
import { downloadRoomingListPng } from '../utils/roomingListImage';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fullName(p) {
  return [p.firstName, p.surname].filter(Boolean).join(' ') || p.email || '—';
}

function fmtDate(str) {
  if (!str) return '';
  const parts = String(str).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : String(str);
}

// One row per guest, grouped by room, for hotel check-in.
function exportCheckInSheet({ layout, participants, groundStaffList, request }) {
  const headers = ['Room', 'Full Name', 'Traveller Type', 'Date of Birth', 'Nationality',
    'Passport Number', 'Issuing Country', 'Passport Expiry'];
  const rows = [];
  const assignments = layout?.assignments || {};
  const byId = new Map(participants.map((p) => [p.id, p]));
  const guestRow = (room, p) => [
    room,
    [p.firstName, p.middleNames, p.surname].filter(Boolean).join(' ') || p.email || '',
    p.travellerType || '',
    fmtDate(p.dateOfBirth),
    p.nationality || '',
    p.passportNumber || '',
    p.countryOfIssue || '',
    fmtDate(p.passportExpiry),
  ];

  (layout?.rooms || []).forEach((room) => {
    (assignments[room.id] || []).forEach((pid) => {
      const p = byId.get(pid);
      if (p) rows.push(guestRow(room.label, p));
    });
  });

  const staffAssignments = layout?.staffAssignments || {};
  (layout?.staffRooms || []).forEach((room) => {
    (staffAssignments[room.id] || []).forEach((s) => {
      // Assignments only store id + name; pull the rest from the ground staff profile.
      const profile = groundStaffList.find((g) => g.id === s.id) || {};
      rows.push([room.label, profile.name || s.name || '', 'MST Staff',
        fmtDate(profile.dateOfBirth), profile.nationality || '', profile.passportNumber || '',
        profile.countryOfIssue || '', fmtDate(profile.passportExpiry)]);
    });
  });

  const assignedIds = new Set(Object.values(assignments).flat());
  participants
    .filter((p) => !assignedIds.has(p.id))
    .forEach((p) => rows.push(guestRow('Unassigned', p)));

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  ws['!cols'] = [16, 28, 16, 14, 16, 18, 18, 16].map((wch) => ({ wch }));
  ws['!autofilter'] = { ref: `A1:H${rows.length + 1}` };
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Check-in');
  const name = request?.internalRef || request?.tourName || request?.groupName || 'tour';
  XLSX.writeFile(wb, `check-in-${String(name).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.xlsx`);
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

// ── Main component ────────────────────────────────────────────────────────────

export default function StaffRoomingTab({ requestId, request }) {
  const { currentUser } = useAuth();
  const [layout, setLayout]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [participants, setParticipants] = useState([]);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [sending, setSending]       = useState(false);
  const [sent, setSent]             = useState(false);
  const [error, setError]           = useState('');
  const [copied, setCopied]         = useState(false);
  const [exporting, setExporting]   = useState(false);

  // Editing state
  const [rooms, setRooms]           = useState([]);
  const [editMode, setEditMode]     = useState(false);

  // Ground staff + staff rooms
  const [groundStaffList, setGroundStaffList] = useState([]);
  const [staffRooms, setStaffRooms]           = useState([]);
  const [staffAssignments, setStaffAssignments] = useState({});
  const [staffEditMode, setStaffEditMode]     = useState(false);
  const [staffSaving, setStaffSaving]         = useState(false);
  const [staffSaved, setStaffSaved]           = useState(false);
  const [staffError, setStaffError]           = useState('');

  // ── Data loading ────────────────────────────────────────────────────────────

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'requests', requestId, 'rooming', 'layout'), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setLayout(data);
        setRooms((data.rooms || []).map((r) => ({ ...r })));
        setStaffRooms((data.staffRooms || []).map((r) => ({ ...r })));
        setStaffAssignments(data.staffAssignments || {});
      } else {
        setLayout(null);
        setRooms([]);
        setStaffRooms([]);
        setStaffAssignments({});
      }
      setLoading(false);
    });
    return unsub;
  }, [requestId]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'groundStaff'), orderBy('name')),
      (snap) => setGroundStaffList(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnap(collection(db, 'requests', requestId, 'participants'), (snap) => {
      setParticipants(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [requestId]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  function addRoom() {
    setRooms((prev) => [...prev, { id: uid(), label: `Room ${prev.length + 1}`, beds: 2 }]);
  }

  function removeRoom(id) {
    setRooms((prev) => prev.filter((r) => r.id !== id));
  }

  function updateRoom(id, field, val) {
    setRooms((prev) => prev.map((r) => r.id === id ? { ...r, [field]: val } : r));
  }

  async function saveLayout() {
    if (rooms.length === 0) { setError('Add at least one room before saving.'); return; }
    setSaving(true);
    setError('');
    try {
      await setDoc(doc(db, 'requests', requestId, 'rooming', 'layout'), {
        rooms: rooms.map((r) => ({ id: r.id, label: r.label, beds: Number(r.beds) || 2 })),
        assignments: layout?.assignments || {},
        status: layout?.status === 'sent' ? 'sent' : 'draft',
        updatedAt: serverTimestamp(),
        createdAt: layout?.createdAt || serverTimestamp(),
      });
      setSaved(true);
      setEditMode(false);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally { setSaving(false); }
  }

  const tokenId = layout?.tokenId || null;
  // Tours sent before no-login links existed carry an old roomingTokens ID,
  // which only ever worked for signed-in users. Those need a fresh link.
  const [linkLive, setLinkLive] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLinkLive(null);
    isLiveCoachLink(tokenId, requestId).then((live) => { if (!cancelled) setLinkLive(live); });
    return () => { cancelled = true; };
  }, [tokenId, requestId]);
  const roomingLink = tokenId && linkLive ? coachLinkUrl('rooming', tokenId) : null;

  async function ensureLink() {
    if (tokenId && linkLive) return tokenId;
    const newTokenId = await createCoachLink({ requestId, kind: 'rooming', user: currentUser });
    await updateDoc(doc(db, 'requests', requestId, 'rooming', 'layout'), { tokenId: newTokenId });
    return newTokenId;
  }

  async function copyLink() {
    if (linkLive === null) return; // still checking the current link
    if (!roomingLink) {
      // Create the replacement link; the copy button appears once it's live.
      try { await ensureLink(); } catch (err) { console.error(err); setError('Could not create a link.'); }
      return;
    }
    navigator.clipboard.writeText(roomingLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function resendEmail() {
    setSending(true);
    setError('');
    try {
      const linkToken = await ensureLink();
      await addDoc(collection(db, 'roomingNotifications'), {
        requestId,
        contactEmail: request.customerEmail,
        contactName: request.contactName,
        tokenId: linkToken,
        sentAt: serverTimestamp(),
        status: 'pending',
      });
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to resend. Please try again.');
    } finally { setSending(false); }
  }

  async function sendToOrganiser() {
    if (!layout || layout.status === 'sent') return;
    setSending(true);
    try {
      // A no-login link for the coach (functions/coachLinks.js)
      const newTokenId = await createCoachLink({ requestId, kind: 'rooming', user: currentUser });

      await updateDoc(doc(db, 'requests', requestId, 'rooming', 'layout'), {
        status: 'sent',
        sentAt: serverTimestamp(),
        tokenId: newTokenId,
      });

      // Trigger Cloud Function email
      await addDoc(collection(db, 'roomingNotifications'), {
        requestId,
        contactEmail: request.customerEmail,
        contactName: request.contactName,
        tokenId: newTokenId,
        sentAt: serverTimestamp(),
        status: 'pending',
      });
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to send. Please try again.');
    } finally { setSending(false); }
  }

  async function exportRoomingSlide() {
    setExporting(true);
    setError('');
    try {
      await downloadRoomingListPng({ request, layout, participants });
    } catch (err) {
      console.error('Rooming List export failed', err);
      setError('Could not create the Rooming List image. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  async function overrideAssignment(roomId, participantId, action) {
    const current = { ...(layout?.assignments || {}) };
    if (action === 'remove') {
      current[roomId] = (current[roomId] || []).filter((id) => id !== participantId);
    } else {
      // Remove from existing room first
      Object.keys(current).forEach((rid) => {
        current[rid] = (current[rid] || []).filter((id) => id !== participantId);
      });
      current[roomId] = [...(current[roomId] || []), participantId];
    }
    await updateDoc(doc(db, 'requests', requestId, 'rooming', 'layout'), {
      assignments: current,
      updatedAt: serverTimestamp(),
    });
  }

  // ── Staff room handlers ──────────────────────────────────────────────────────

  function addStaffRoom() {
    setStaffRooms((prev) => [...prev, { id: uid(), label: `Staff Room ${prev.length + 1}` }]);
  }

  function removeStaffRoom(id) {
    setStaffRooms((prev) => prev.filter((r) => r.id !== id));
    setStaffAssignments((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  function updateStaffRoom(id, val) {
    setStaffRooms((prev) => prev.map((r) => r.id === id ? { ...r, label: val } : r));
  }

  function assignToStaffRoom(roomId, staffId) {
    const staffMember = groundStaffList.find((s) => s.id === staffId);
    if (!staffMember) return;
    setStaffAssignments((prev) => {
      const next = {};
      Object.entries(prev).forEach(([rid, members]) => {
        next[rid] = members.filter((m) => m.id !== staffId);
      });
      next[roomId] = [...(next[roomId] || []), { id: staffId, name: staffMember.name }];
      return next;
    });
  }

  function removeFromStaffRoom(roomId, staffId) {
    setStaffAssignments((prev) => ({
      ...prev,
      [roomId]: (prev[roomId] || []).filter((m) => m.id !== staffId),
    }));
  }

  async function saveStaffLayout() {
    setStaffSaving(true);
    setStaffError('');
    try {
      await setDoc(doc(db, 'requests', requestId, 'rooming', 'layout'), {
        staffRooms,
        staffAssignments,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setStaffSaved(true);
      setStaffEditMode(false);
      setTimeout(() => setStaffSaved(false), 2500);
    } catch (err) {
      console.error(err);
      setStaffError('Failed to save staff rooms. Please try again.');
    } finally { setStaffSaving(false); }
  }

  function cancelStaffEdit() {
    setStaffEditMode(false);
    setStaffRooms((layout?.staffRooms || []).map((r) => ({ ...r })));
    setStaffAssignments({ ...(layout?.staffAssignments || {}) });
    setStaffError('');
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center text-[13px] text-[#9CA3AF]">Loading…</div>;
  }

  const isDraft  = !layout || layout.status === 'draft';
  const isSent   = layout?.status === 'sent';
  const isSubmitted = layout?.status === 'submitted' || layout?.status === 'finalised';
  const assignments = layout?.assignments || {};
  const allAssignedIds = new Set(Object.values(assignments).flat());
  const unassigned = participants.filter((p) => !allAssignedIds.has(p.id));

  return (
    <div className="space-y-4">

      {/* Status banner */}
      {isSent && !isSubmitted && (
        <div className="flex items-center gap-3 p-4 bg-[#EBF2FC] border border-[#93C5FD] rounded-[14px]">
          <svg className="w-5 h-5 text-pb flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          <div className="flex-1">
            <p className="text-[13px] font-bold text-pb">Sent to organiser</p>
            <p className="text-[12px] text-[#2563EB]">Waiting for {request.contactName || 'the organiser'} to assign participants.</p>
          </div>
        </div>
      )}
      {isSubmitted && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-[14px]">
          <svg className="w-5 h-5 text-[#16A34A] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          <div>
            <p className="text-[13px] font-bold text-[#166534]">Assignments submitted by organiser</p>
            <p className="text-[12px] text-[#16A34A]">Room assignments are shown below. You can override individual placements.</p>
          </div>
        </div>
      )}

      {/* Link + resend — shown once sent */}
      {(isSent || isSubmitted) && (
        <div className="flex items-center justify-between gap-3 p-3 bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB]">
          <div className="min-w-0">
            <p className="text-[12px] font-semibold text-[#374151]">Coach rooming link (no login needed)</p>
            <p className="text-[11px] text-[#9CA3AF] truncate">
              {roomingLink || (linkLive === false ? 'The old link needs a login. Create a new one to share.' : 'Checking link…')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={copyLink}
              className="text-[11px] font-semibold text-pb hover:text-navy transition"
            >
              {copied ? '✓ Copied' : roomingLink ? 'Copy link' : linkLive === false ? 'Create new link' : 'Copy link'}
            </button>
            <span className="text-[#E5E7EB]">|</span>
            <button
              onClick={resendEmail}
              disabled={sending}
              className="text-[11px] font-semibold text-pb hover:text-navy transition disabled:opacity-60"
            >
              {sending ? 'Sending…' : sent ? '✓ Sent' : 'Re-send email'}
            </button>
          </div>
        </div>
      )}

      {layout?.rooms?.length > 0 && !editMode && (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[13px] font-bold text-[#111827]">Rooming List slide</p>
            <p className="text-[12px] text-[#6B7280]">A ready-made 1920×1080 image of every room and who is in it (MST staff rooms included) — drop it straight into the Canva welcome presentation.</p>
          </div>
          <button onClick={exportRoomingSlide} disabled={exporting}
            className="px-3.5 py-[7px] text-[12.5px] font-bold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60 shrink-0">
            {exporting ? 'Creating…' : 'Download for Canva (PNG)'}
          </button>
        </div>
      )}

      {/* Room layout builder */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden">
        <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
          <h3 className="text-[14px] font-bold text-[#111827]">Room Layout</h3>
          <div className="flex items-center gap-2">
            {saved && <span className="text-[12px] text-[#16A34A] font-semibold">Saved ✓</span>}
            {sent  && <span className="text-[12px] text-pb font-semibold">Sent ✓</span>}
            {!editMode && layout?.rooms?.length > 0 && (
              <button onClick={() => exportCheckInSheet({ layout, participants, groundStaffList, request })}
                title="Spreadsheet of every room with names, DOB and passport details for hotel check-in"
                className="px-3 py-1.5 text-[12px] font-semibold text-[#374151] border border-[#E5E7EB] rounded-[8px] hover:bg-surface transition">
                Export check-in list
              </button>
            )}
            {!editMode ? (
              <button onClick={() => setEditMode(true)}
                className="px-3 py-1.5 text-[12px] font-semibold text-pb border border-pb/30 rounded-[8px] hover:bg-pb/5 transition">
                {layout ? 'Edit layout' : 'Build layout'}
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => { setEditMode(false); setRooms((layout?.rooms || []).map((r) => ({ ...r }))); setError(''); }}
                  className="px-3 py-1.5 text-[12px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-[8px] hover:bg-surface transition">
                  Cancel
                </button>
                <button onClick={saveLayout} disabled={saving}
                  className="px-3 py-1.5 text-[12px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
                  {saving ? 'Saving…' : 'Save layout'}
                </button>
              </div>
            )}
          </div>
        </div>

        {error && <p className="px-[18px] py-2 text-[12px] text-dr bg-[#FEF2F2]">{error}</p>}

        <div className="p-[18px]">
          {editMode ? (
            <div className="space-y-3">
              {rooms.map((room, i) => (
                <div key={room.id} className="flex items-center gap-3 p-3 bg-[#F9FAFB] rounded-[10px]">
                  <div className="w-7 h-7 rounded-full bg-pb/10 text-pb flex items-center justify-center text-[11px] font-bold flex-shrink-0">{i + 1}</div>
                  <input
                    value={room.label}
                    onChange={(e) => updateRoom(room.id, 'label', e.target.value)}
                    placeholder="Room name"
                    className="flex-1 border border-[#E5E7EB] rounded-[8px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white min-w-0"
                  />
                  <div className="flex items-center gap-1.5">
                    <label className="text-[11px] font-semibold text-[#6B7280] whitespace-nowrap">Beds</label>
                    <input
                      type="number" min="1" max="20"
                      value={room.beds}
                      onChange={(e) => updateRoom(room.id, 'beds', e.target.value)}
                      className="w-16 border border-[#E5E7EB] rounded-[8px] px-2.5 py-1.5 text-[13px] text-center focus:outline-none focus:ring-2 focus:ring-pb bg-white"
                    />
                  </div>
                  <button onClick={() => removeRoom(room.id)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-dr text-lg flex-shrink-0">×</button>
                </div>
              ))}
              <button onClick={addRoom}
                className="flex items-center gap-1.5 text-[13px] font-semibold text-pb hover:text-navy transition">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                Add room
              </button>
            </div>
          ) : layout?.rooms?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(layout.rooms).map((room) => {
                const occupantIds = assignments[room.id] || [];
                const occupants = occupantIds.map((pid) => participants.find((p) => p.id === pid)).filter(Boolean);
                return (
                  <div key={room.id} className="border border-[#E5E7EB] rounded-[10px] p-3">
                    <p className="text-[12px] font-bold text-[#111827] mb-0.5">{room.label}</p>
                    <p className="text-[11px] text-[#9CA3AF] mb-2">{room.beds} bed{room.beds !== 1 ? 's' : ''} · {occupants.length}/{room.beds}</p>
                    <div className="space-y-1">
                      {occupants.map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-1">
                          <span className="text-[11px] text-[#374151] truncate">{fullName(p)}</span>
                          {isSubmitted && (
                            <button onClick={() => overrideAssignment(room.id, p.id, 'remove')}
                              className="text-[10px] text-[#9CA3AF] hover:text-dr flex-shrink-0 ml-1">×</button>
                          )}
                        </div>
                      ))}
                      {occupants.length === 0 && (
                        <p className="text-[11px] text-[#D1D5DB] italic">Empty</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[13px] text-[#9CA3AF]">No room layout built yet. Click "Build layout" to get started.</p>
          )}
        </div>
      </div>

      {/* Unassigned participants (after organiser submits) */}
      {isSubmitted && unassigned.length > 0 && (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-[18px]">
          <p className="text-[13px] font-bold text-[#111827] mb-3">
            {unassigned.length} unassigned participant{unassigned.length !== 1 ? 's' : ''}
          </p>
          <div className="space-y-2">
            {unassigned.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 p-2 bg-[#F9FAFB] rounded-[8px]">
                <span className="text-[13px] text-[#374151]">{fullName(p)}</span>
                <select
                  className="border border-[#E5E7EB] rounded-[7px] px-2 py-1 text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-pb"
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) overrideAssignment(e.target.value, p.id, 'add'); }}
                >
                  <option value="">Assign to room…</option>
                  {(layout?.rooms || []).map((r) => {
                    const count = (assignments[r.id] || []).length;
                    return (
                      <option key={r.id} value={r.id} disabled={count >= r.beds}>
                        {r.label} ({count}/{r.beds})
                      </option>
                    );
                  })}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MST Staff Rooms */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden">
        <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
          <div>
            <h3 className="text-[14px] font-bold text-[#111827]">MST Staff Rooms</h3>
            <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">Internal only — organiser can view but cannot edit.</p>
          </div>
          <div className="flex items-center gap-2">
            {staffSaved && <span className="text-[12px] text-[#16A34A] font-semibold">Saved ✓</span>}
            {!staffEditMode ? (
              <button onClick={() => setStaffEditMode(true)}
                className="px-3 py-1.5 text-[12px] font-semibold text-pb border border-pb/30 rounded-[8px] hover:bg-pb/5 transition">
                {staffRooms.length > 0 ? 'Edit staff rooms' : 'Add staff rooms'}
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={cancelStaffEdit}
                  className="px-3 py-1.5 text-[12px] font-semibold text-[#6B7280] border border-[#E5E7EB] rounded-[8px] hover:bg-surface transition">
                  Cancel
                </button>
                <button onClick={saveStaffLayout} disabled={staffSaving}
                  className="px-3 py-1.5 text-[12px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
                  {staffSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>
        </div>

        {staffError && <p className="px-[18px] py-2 text-[12px] text-dr bg-[#FEF2F2]">{staffError}</p>}

        <div className="p-[18px]">
          {staffEditMode ? (
            <div className="space-y-3">
              {staffRooms.map((room, i) => (
                <div key={room.id} className="border border-[#E5E7EB] rounded-[10px] p-3 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-navy/10 text-navy flex items-center justify-center text-[11px] font-bold flex-shrink-0">{i + 1}</div>
                    <input
                      value={room.label}
                      onChange={(e) => updateStaffRoom(room.id, e.target.value)}
                      placeholder="Room name"
                      className="flex-1 border border-[#E5E7EB] rounded-[8px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white min-w-0"
                    />
                    <button onClick={() => removeStaffRoom(room.id)} className="w-7 h-7 flex items-center justify-center text-[#9CA3AF] hover:text-dr text-lg flex-shrink-0">×</button>
                  </div>
                  <div className="pl-10 space-y-1">
                    {(staffAssignments[room.id] || []).map((s) => (
                      <div key={s.id} className="flex items-center gap-2">
                        <span className="text-[12px] text-[#374151] flex-1">{s.name}</span>
                        <button onClick={() => removeFromStaffRoom(room.id, s.id)}
                          className="text-[11px] text-[#9CA3AF] hover:text-dr">×</button>
                      </div>
                    ))}
                    {groundStaffList.length > 0 ? (
                      <select
                        className="mt-1 border border-[#E5E7EB] rounded-[7px] px-2 py-1 text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-pb"
                        value=""
                        onChange={(e) => { if (e.target.value) assignToStaffRoom(room.id, e.target.value); }}
                      >
                        <option value="">+ Assign staff member…</option>
                        {groundStaffList
                          .filter((s) => !(staffAssignments[room.id] || []).some((a) => a.id === s.id))
                          .map((s) => <option key={s.id} value={s.id}>{s.name}</option>)
                        }
                      </select>
                    ) : (
                      <p className="text-[11.5px] text-[#9CA3AF]">No ground staff profiles yet — add them in the Ground Staff section first.</p>
                    )}
                  </div>
                </div>
              ))}
              <button onClick={addStaffRoom}
                className="flex items-center gap-1.5 text-[13px] font-semibold text-pb hover:text-navy transition">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                Add staff room
              </button>
            </div>
          ) : staffRooms.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {staffRooms.map((room) => {
                const occupants = staffAssignments[room.id] || [];
                return (
                  <div key={room.id} className="border border-[#E5E7EB] rounded-[10px] p-3">
                    <p className="text-[12px] font-bold text-[#111827] mb-2">{room.label}</p>
                    <div className="space-y-1">
                      {occupants.length === 0 ? (
                        <p className="text-[11px] text-[#D1D5DB] italic">No staff assigned</p>
                      ) : occupants.map((s) => (
                        <p key={s.id} className="text-[12px] text-[#374151]">{s.name}</p>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[13px] text-[#9CA3AF]">No staff rooms added yet. Click "Add staff rooms" to set up rooms for ground staff.</p>
          )}
        </div>
      </div>

      {/* Send to organiser */}
      {layout && isDraft && !editMode && (
        <button onClick={sendToOrganiser} disabled={sending || !layout?.rooms?.length}
          className="w-full py-3 bg-pb text-white font-bold text-[13px] rounded-[12px] transition hover:bg-navy disabled:opacity-50 flex items-center justify-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          {sending ? 'Sending…' : `Send room layout to ${request.contactName || 'organiser'}`}
        </button>
      )}
    </div>
  );
}
